import type { Charges, Collaborateur, Gerant, Parametres, Resultats } from './types'

// ═══════════════════════════════════════════════════════════════════════════════
// RÈGLES DE CALCUL
// ───────────────────────────────────────────────────────────────────────────────
//  1. Charges fixes   → saisies en € HT / MOIS
//  2. Masse salariale → coût total pour l'employeur mensuel HT (saisi directement)
//  3. Seuil HT        → total charges HT / facteur produits
//  4. Seuil TTC       → Seuil HT × (1 + TVA/100)   ← ce que le salon doit encaisser
//  5. Coût-minute/h   → exprimés en € TTC
//
//  PRODUITS TECHNIQUES UTILISÉS (% du CA)
//    Coût proportionnel au CA → AUGMENTE le seuil
//
//  FORMULE :
//    facteur = 1 − pctTech
//    Seuil HT  = TotalChargesHT / facteur
//    Seuil TTC = Seuil HT × (1 + TVA/100)
// ═══════════════════════════════════════════════════════════════════════════════

export const formatEuros = (v: number) =>
  new Intl.NumberFormat('fr-FR',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(v)
export const formatEurosCents = (v: number) =>
  new Intl.NumberFormat('fr-FR',{style:'currency',currency:'EUR',minimumFractionDigits:2,maximumFractionDigits:2}).format(v)

// ── Charges fixes par catégorie (€ HT / mois) ─────────────────────────────────
export function chargesParCategorie(c: Charges): Record<string, number> {
  return {
    'Structure':    c.loyer+c.chargesLocatives+c.credit+c.leasingMateriel+c.cfe+c.taxeFonciere+c.electricite+c.eau+c.assurance+c.entretien+c.alarme,
    'Déplacements': c.fraisKilometriques+c.achatVehicule+c.leasingVehicule+c.essence+c.assuranceVehicule+c.transportAchat+c.autresVehicule,
    'Métier':       c.petitMateriel+c.grosMateriel+c.produitsTechniquesCharges+c.vetementsProf+c.logicielCoiffure+c.decoration+c.autresMetier,
    'Admin':        c.expertComptable+c.fraisJuridiques+c.honorairesSocial+c.fraisBancaires+c.fraisTPE+c.logicielCaisse+c.telephoneFixe+c.telephoneMobile+c.hebergementWeb+c.fournitures+c.presse+c.cadeauxClients+c.cotisationsGroupement,
    'Social sal.':  c.medecine+c.mutuelleSalaries+c.epargneSalariale+c.adsams+c.taxeApprentissage+c.opco,
    'Marketing':    c.coaching+c.marketingDigital+c.communication+c.formationCoiffure+c.formationManagement,
    'Droits':       c.sacem+c.spre,
    'Autres':       c.soustraitance+c.fraisReception+c.autresCharges,
  }
}
export function calculerChargesFixesMensuelles(c: Charges): number {
  return Object.values(chargesParCategorie(c)).reduce((a,b) => a+b, 0)
}

// ── Collaborateurs ────────────────────────────────────────────────────────────
// Un seul champ : coutTotal = coût total pour l'employeur mensuel HT (saisi directement)
export function heuresProductivesCollab(c: Collaborateur): number {
  return c.heuresMois * (c.tauxEfficacite / 100)
}
export function seuilCollaborateur(c: Collaborateur, caMinTTC: number, totalH: number): number {
  if (totalH === 0) return 0
  return caMinTTC * (heuresProductivesCollab(c) / totalH)
}
export function perfCollab(c: Collaborateur, caMinTTC: number, totalH: number): number {
  if (!c.caReel || c.caReel === 0) return 0
  const s = seuilCollaborateur(c, caMinTTC, totalH)
  return s === 0 ? 0 : (c.caReel / s) * 100
}

// ── Calcul revente pour un collaborateur ──────────────────────────────────────
// venteMoisTTC  = montant des ventes TTC réalisées dans le mois (saisie directe)
// venteHT       = venteMoisTTC ÷ (1 + TVA/100)
// achatHT       = venteHT ÷ coeffRevente  (coût fournisseur déduit)
// prime brute   = venteHT × primePct%
// charges       = primeBrute × tauxChargesPrime%
export function calcRevente(c: Collaborateur, tauxTVA = 20) {
  const venteTTC  = c.venteMoisTTC ?? 0
  const coeff     = c.coeffRevente ?? 2.2
  const diviseur  = 1 + tauxTVA / 100
  const venteHT   = diviseur > 0 ? venteTTC / diviseur : 0
  const achatHT   = coeff > 0 ? venteHT / coeff : 0
  const primePct   = (c.primePct ?? 10) / 100
  const chargesPct = (c.tauxChargesPrime ?? 45) / 100
  // Base de calcul de la prime : HT (défaut) ou TTC selon le choix
  const basePrime  = c.primeBaseTTC ? venteTTC : venteHT
  const primeBrute     = basePrime * primePct
  const chargesPrime   = primeBrute * chargesPct
  const coutPrimeSalon = primeBrute + chargesPrime
  const margeBrute     = venteHT - achatHT
  const beneficeNet    = margeBrute - coutPrimeSalon
  return { venteTTC, venteHT, achatHT, primeBrute, chargesPrime, coutPrimeSalon, margeBrute, beneficeNet, basePrime, primeBaseTTC: !!c.primeBaseTTC }
}

// ── Coût total d'un collaborateur incluant prime revente ──────────────────────
export function coutAvecPrime(c: Collaborateur): number {
  const base = c.coutTotal
  const rv = calcRevente(c)
  return rv.coutPrimeSalon > 0 ? base + rv.coutPrimeSalon : base
}

// ── Calcul principal ───────────────────────────────────────────────────────────
export function calculerResultats(
  charges: Charges, collabs: Collaborateur[], gerant: Gerant, p: Parametres
): Resultats {
  const cat                    = chargesParCategorie(charges)
  const chargesFixesMensuelles = Object.values(cat).reduce((a,b) => a+b, 0)

  // Masse salariale = collaborateurs uniquement (hors gérant)
  const masseSalarialeCollabs   = collabs.reduce((s,c) => s + coutAvecPrime(c), 0)
  // Coût gérant toujours inclus dans le seuil (il doit être rémunéré)
  const coutGerant              = gerant.coutTotal
  const masseSalarialeMensuelle = masseSalarialeCollabs + coutGerant

  const totalChargesHT         = chargesFixesMensuelles + masseSalarialeMensuelle + p.objectifBenefice

  // Heures productives
  const heuresGerantProductives = gerant.heuresMois * (gerant.tauxEfficacite / 100)
  const heuresCollabs           = collabs.reduce((s,c) => s + heuresProductivesCollab(c), 0)
  const heuresProductivesTotales = heuresCollabs + heuresGerantProductives   // gérant actif

  // Heures sans gérant (mode passif)
  const heuresSansGerant = heuresCollabs

  // Facteur produits
  const pctTech   = p.pctProduitsTechUtilises / 100
  const facteur   = 1 - pctTech
  const safeF     = facteur > 0.01 ? facteur : 1

  const coefTVA   = 1 + p.tauxTVA / 100

  // ── Mode ACTIF — gérant génère du CA (heures totales = collabs + gérant) ────
  const caMinimumHT  = totalChargesHT / safeF
  const caMinimumTTC = caMinimumHT * coefTVA

  // ── Mode PASSIF — l'équipe couvre tout (gérant ne génère pas de CA) ──────────
  // Total inchangé (gérant est quand même payé) mais heures = collabs seulement
  const caMinimumSansGerantTTC = caMinimumTTC  // même seuil financier…

  // Impact produits
  const coutProduitsTechMensuel = caMinimumTTC * pctTech

  // Seuils temporels — ACTIF
  const seuilQuotidienTTC = p.joursTravailles > 0 ? caMinimumTTC / p.joursTravailles : 0
  const seuilHoraireTTC   = heuresProductivesTotales > 0 ? caMinimumTTC / heuresProductivesTotales : 0
  const seuilMinuteTTC    = heuresProductivesTotales > 0 ? caMinimumTTC / (heuresProductivesTotales * 60) : 0

  // Seuils temporels — PASSIF (basés sur heures équipe sans gérant)
  const seuilQuotidienSansGerantTTC = p.joursTravailles > 0 ? caMinimumSansGerantTTC / p.joursTravailles : 0
  const seuilMinuteSansGerantTTC    = heuresSansGerant > 0 ? caMinimumSansGerantTTC / (heuresSansGerant * 60) : 0

  // Coût-minute TTC
  const minProd = heuresProductivesTotales * 60
  const coutMinuteTheoriqueTTC = minProd > 0 ? caMinimumTTC / minProd : 0
  const coutMinuteReelTTC      = p.tauxRemplissage > 0 ? coutMinuteTheoriqueTTC / (p.tauxRemplissage/100) : 0
  const ecartPct               = coutMinuteTheoriqueTTC > 0
    ? ((coutMinuteReelTTC - coutMinuteTheoriqueTTC) / coutMinuteTheoriqueTTC) * 100 : 0

  // ── Seuils individuels par collaborateur (sans le gérant) ────────────────────
  // Répartition des charges fixes AU PRORATA DU POIDS SALARIAL
  const masseSalarialePure = collabs.reduce((s, c) => s + c.coutTotal, 0)

  const seuilsIndividuels = collabs.map(c => {
    const hCollab = heuresProductivesCollab(c)
    const ratio = masseSalarialePure > 0
      ? c.coutTotal / masseSalarialePure
      : (collabs.length > 0 ? 1 / collabs.length : 1)
    const coutDirect          = coutAvecPrime(c)
    const quotePartCharges    = chargesFixesMensuelles * ratio
    const quotePartBenef      = p.objectifBenefice * ratio
    const baseHT              = coutDirect + quotePartCharges + quotePartBenef
    const seuilHT             = safeF > 0.01 ? baseHT / safeF : baseHT
    const seuilTTC            = seuilHT * coefTVA
    const coutMinuteIndivTTC  = hCollab > 0 ? seuilTTC / (hCollab * 60) : 0
    return {
      id: c.id,
      seuilHT,
      seuilTTC,
      coutMinuteIndivTTC,
      coutDirect,
      quotePartCharges,
      quotePartBenef,
      ratioPct: Math.round(ratio * 100),
    }
  })

  return {
    chargesFixesMensuelles, masseSalarialeMensuelle, totalChargesHT,
    coutProduitsTechMensuel,
    caMinimumHT, caMinimumTTC, caMinimumSansGerantTTC,
    heuresProductivesTotales,
    seuilQuotidienTTC, seuilHoraireTTC, seuilMinuteTTC,
    seuilQuotidienSansGerantTTC, seuilMinuteSansGerantTTC,
    heuresSansGerant,
    coutMinuteTheoriqueTTC, coutMinuteReelTTC, ecartPct,
    chargesParCategorie: cat,
    seuilsIndividuels,
  }
}

export function generateAccessCode(): string {
  const l='ABCDEFGHJKLMNPQRSTUVWXYZ', d='23456789', a=l+d
  let c=l[~~(Math.random()*l.length)]+l[~~(Math.random()*l.length)]+d[~~(Math.random()*d.length)]+d[~~(Math.random()*d.length)]
  for(let i=0;i<4;i++) c+=a[~~(Math.random()*a.length)]
  return `SALON-${c.split('').sort(()=>Math.random()-.5).join('')}`
}
